import { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { FormState, FieldState } from 'formstate';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { getTermsUrl } from '@config/index';
import Modal from '@components/Modal/Modal';
import Input from '@components/Input/Input';
import Textarea from '@components/Textarea/Textarea';
import Select from '@components/Select/Select';
import Checkbox from '@components/Checkbox/Checkbox';
import Button from '@components/Button/Button';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import { required, emailValid, phone, requiredConsent, min, max } from '@features/userPanel/mobx/stores/validators';
import { TCountry } from '@typescript/regional';
import NextSurveyApi from '@features/api/nextApiService/nextSurveyApi';
import alertsStore from '@context/global/mobx/alertStore';
import GoogleAdsGenerateLead from '@reaffiliates/GoogleAds/GoogleAdsGenerateLead';
import styles from './DesignStorModal.styles';
import useBodyOverflow from '@hooks/useBodyOverflow';
type PropsType = {
  isOpen: boolean;
  onClose: () => void;
};
const DesignStorModal = ({
  isOpen,
  onClose
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    locale,
    defaultLocale
  } = useRouter();
  const regulationsHref = getTermsUrl(locale, defaultLocale);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [consentValue, setConsentValue] = useState(false);
  useBodyOverflow(isOpen, 'designStorModal', true, true);

  // Form state
  const quoteForm = useMemo(() => new FormState({
    fullName: new FieldState<string>('').validators(required()).disableAutoValidation(),
    email: new FieldState<string>('').validators(required(), emailValid()).disableAutoValidation(),
    phoneCountryCode: new FieldState<string>('PL'),
    phoneNumber: new FieldState<string>('').validators(required(), phone('PL' as TCountry)).disableAutoValidation(),
    comment: new FieldState<string>('').validators(min(5), max(4000)).disableAutoValidation(),
    consent: new FieldState<boolean>(false).validators(requiredConsent()).enableAutoValidation()
  }), []);

  // Phone country codes options
  const phoneCountryCodes = [{
    name: '+48',
    value: 'PL'
  }];

  // Reset success state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsSuccess(false);
    }
  }, [isOpen]);

  // Synchronize local consent state with FormState
  useEffect(() => {
    quoteForm.$.consent.onChange(consentValue);
  }, [consentValue, quoteForm]);
  const handleClose = () => {
    setIsSuccess(false);
    onClose();
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const validationResult = await quoteForm.validate();
      if (validationResult.hasError) {
        setIsSubmitting(false);
        return;
      }

      // Przygotowanie numeru telefonu
      const countryCode = quoteForm.$.phoneCountryCode.value;
      const fullPhoneNumber = `${countryCode} ${quoteForm.$.phoneNumber.value}`;

      // Wysłanie zapytania
      const response = await NextSurveyApi.submitDesignStor({
        fullName: quoteForm.$.fullName.value,
        email: quoteForm.$.email.value,
        phoneNumber: fullPhoneNumber,
        comment: quoteForm.$.comment.value || ''
      });
      if (response.error) {
        alertsStore.pushAlert({
          message: response.message ? t(response.message) : t('quoteRequest.error.submitFailed'),
          display: true
        });
        setIsSubmitting(false);
        return;
      }
      GoogleAdsGenerateLead('design_stor');
      setIsSuccess(true);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alertsStore.pushAlert({
        message: t('quoteRequest.error.submitFailed'),
        display: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Modal active={isOpen} closeFunc={handleClose} heading={t('designStor.title')} closeOnOutsideClick fullScreen={true} addContentSx={styles.modalContainer(isSuccess)} addSx={styles.modalHeading} headingAddSx={styles.modalHeadingText}>
      <div sx={styles.modalContent} data-testid="design-stor-modal-content">
        {isSuccess ? (/* Success Screen */
      <>
            <div sx={styles.successContainer} data-testid="design-stor-modal-success-container">
              <div sx={styles.successIconContainer} data-testid="design-stor-modal-success-icon-container">
                <div sx={styles.successIcon} data-testid="design-stor-modal-success-icon-wrapper">
                  <Icon icon="Check" size="64px" color="darkGrey700" cy="design-stor-modal-success-icon" />
                </div>
              </div>
              <div sx={styles.textContainer} data-testid="design-stor-modal-success-text-container">
                <Heading h={5} addSx={styles.successTitle} data-testid="design-stor-modal-success-title">
                  {t('designStor.success.title')}
                </Heading>
                <p sx={styles.successMessage} data-testid="design-stor-modal-success-message">
                  {t('designStor.success.message')}
                </p>
              </div>
            </div>
            <div sx={styles.successFooter} data-testid="design-stor-modal-success-footer">
              <Button variant="default" variantColor="dark" onClick={handleClose} addSx={styles.closeButton} cy="design-stor-modal-success-close-button">
                {t('designStor.success.closeButton')}
              </Button>
            </div>
          </>) : (/* Form Screen */
      <>
            {/* Scrollable Content */}
            <div sx={styles.scrollableContent} data-testid="design-stor-modal-scrollable-content">
              {/* info section */}
              <div sx={styles.infoContainer} data-testid="design-stor-modal-info-container">
                <div sx={styles.infoContent} data-testid="design-stor-modal-info-content-1">
                  <Icon icon="Resize" size="24px" color="darkGrey700" cy="design-stor-modal-info-icon-1" />
                  <p sx={styles.infoText} data-testid="design-stor-modal-info-text-1">
                    {t('designStor.infoText1')}
                  </p>
                </div>
                <div sx={styles.infoContent} data-testid="design-stor-modal-info-content-2">
                  <Icon icon="GroupMonitor" size="24px" color="darkGrey700" cy="design-stor-modal-info-icon-2" />
                  <p sx={styles.infoText} data-testid="design-stor-modal-info-text-2">
                    {t('designStor.infoText2')}
                  </p>
                </div>
                <div sx={styles.infoContent} data-testid="design-stor-modal-info-content-3">
                  <Icon icon="Shield" size="24px" color="darkGrey700" cy="design-stor-modal-info-icon-3" />
                  <p sx={styles.infoText} data-testid="design-stor-modal-info-text-3">
                    {t('designStor.infoText3')}
                  </p>
                </div>
              </div>
              <div sx={styles.smallInfoContainer} data-testid="design-stor-modal-small-info-container">
                <p sx={styles.smallInfoText} data-testid="design-stor-modal-small-info-text">
                  {t('designStor.infoText')}
                </p>
              </div>

              {/* Inquiry Content Section */}
              <div sx={styles.section} data-testid="design-stor-modal-data-section">
                <div sx={styles.sectionTitleContainer} data-testid="design-stor-modal-data-section-title-container">
                  <Heading h={4} headingType={4} addSx={styles.sectionTitle} data-testid="design-stor-modal-data-section-title">
                    {t('designStor.yourData')}
                  </Heading>
                  <p sx={styles.formInfo} data-testid="design-stor-modal-form-info">
                    {t('designStor.formInfo')}
                  </p>
                </div>

                <Input field={quoteForm.$.email} placeholder={t('quoteRequest.email')} addSx={styles.emailInput} cy="design-stor-modal-email-input" />
                <Input field={quoteForm.$.fullName} placeholder={t('quoteRequest.fullName')} addSx={styles.fullNameInput} cy="design-stor-modal-full-name-input" />
                <div sx={styles.phoneContainer} data-testid="design-stor-modal-phone-container">
                  <Select value={quoteForm.$.phoneCountryCode.value} onChange={value => quoteForm.$.phoneCountryCode.onChange(value.toString())} options={phoneCountryCodes} addSx={styles.phoneCountryCodeSelect} disabled={true} cy="design-stor-modal-phone-country-code-select" />
                  <Input field={quoteForm.$.phoneNumber} placeholder={t('quoteRequest.phoneNumber')} addSx={styles.phoneNumberInput} cy="design-stor-modal-phone-number-input" />
                </div>
              </div>
              {/* question section */}
              <div sx={styles.section} data-testid="design-stor-modal-question-section">
                <Heading h={4} headingType={4} addSx={styles.sectionTitle} data-testid="design-stor-modal-question-section-title">
                  {t('designStor.sectionTitle')}
                </Heading>
                <Textarea field={quoteForm.$.comment} placeholder={t('designStor.questionPlaceholder')} bottomDescription={t('designStor.questionText')} maxLength={4000} addSx={styles.questionTextarea} cy="design-stor-modal-question-textarea" />
              </div>
            </div>

            {/* Footer - Always Visible at Bottom */}
            <div sx={styles.footer} data-testid="design-stor-modal-footer">
              <div sx={styles.consentContainer} data-testid="design-stor-modal-consent-container">
                <Checkbox value={consentValue} onChange={setConsentValue} showError={!!quoteForm.$.consent.error} error={quoteForm.$.consent.error || ''} addSx={styles.checkboxContainer} cy="design-stor-modal-consent-checkbox">
                  <span sx={styles.consentText} data-testid="design-stor-modal-consent-text">
                    {t('quoteRequest.consent')}{' '}
                    <a href={regulationsHref} target="_blank" rel="noopener noreferrer" sx={styles.regulationsLink} data-testid="design-stor-modal-regulations-link">
                      {t('quoteRequest.regulationsLink')}
                    </a>
                  </span>
                </Checkbox>
              </div>
              <Button variant="default" variantColor="brand" onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting} addSx={styles.submitButton} cy="design-stor-modal-submit-button">
                {t('designStor.btnText')}
              </Button>
            </div>
          </>)}
      </div>
    </Modal>;
};
export default observer(DesignStorModal);